import { useCallback, useEffect, useRef, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useCollectionBinding,
  useSlotGroup,
  EditableCollectionBinding,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { clean, money, num, range, stagger, titleCase } from '../utils/format';
import { useRawProperty, useReducedMotion } from '../utils/hooks';
import { PATHS, useSiteFlags } from '../utils/site';

export interface Floorplan {
  floorplan_id?: string;
  floorplan_name?: string;
  floorplan_image_url?: string;
  floorplan_beds_min?: string;
  floorplan_baths_min?: string;
  floorplan_sqft_min?: string;
  floorplan_sqft_max?: string;
  floorplan_price_min?: string;
  floorplan_available_units?: string;
}

const applyCtaDef: SlotGroupDefinition = {
  label: 'Apply Now Button',
  fields: {
    label: { label: 'Button Text', type: 'text', defaultValue: 'Apply Now' },
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_apply_now_url',
      suggestedFields: ['property_apply_now_url', 'property_online_leasing_url'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
    vibesite_event: { label: 'Tracked Event', type: 'event', defaultValue: 'apply_now_clicked' },
  },
};

const exploreCtaDef: SlotGroupDefinition = {
  label: 'Explore Floor Plans Button',
  fields: {
    label: { label: 'Button Text', type: 'text', defaultValue: 'Explore Floor Plans' },
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const mapLinkDef: SlotGroupDefinition = {
  label: 'Interactive Map Link',
  description: 'Shown only when the property has a SightMap.',
  fields: {
    label: { label: 'Link Text', type: 'text', defaultValue: 'View Availability on the Map' },
    url: { label: 'URL', type: 'url', defaultValue: '/interactive-map' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

export const PLAN_OUTLINE = (
  <svg className="fpcard__outline" viewBox="0 0 160 120" aria-hidden="true">
    <rect x="8" y="8" width="144" height="104" rx="4" />
    <path d="M8 60h64M72 8v104M72 60h80M112 60v52" />
    <path d="M40 8v22M40 40v20" strokeDasharray="3 5" />
  </svg>
);

export interface PlanCard {
  key: string;
  name: string;
  img: string | null;
  beds: number | null;
  baths: number | null;
  sqft: number | null;
  sqftMax: number | null;
  price: number | null;
  avail: number;
}

export function toPlanCards(plans: Floorplan[]): PlanCard[] {
  return plans
    .map((f, i) => ({
      key: f.floorplan_id || String(i),
      name: titleCase(f.floorplan_name),
      img: clean(f.floorplan_image_url),
      beds: num(f.floorplan_beds_min),
      baths: num(f.floorplan_baths_min),
      sqft: num(f.floorplan_sqft_min),
      sqftMax: num(f.floorplan_sqft_max),
      price: num(clean(f.floorplan_price_min)),
      avail: num(f.floorplan_available_units) || 0,
    }))
    .sort((a, b) => (a.beds ?? 99) - (b.beds ?? 99) || (a.sqft ?? 0) - (b.sqft ?? 0));
}

export function FloorplansList() {
  const p = useRawProperty();
  const flags = useSiteFlags();
  const reduced = useReducedMotion();
  const apply = useSlotGroup('floorplanslist.apply_cta', applyCtaDef);
  const explore = useSlotGroup('floorplanslist.explore_cta', exploreCtaDef);
  const mapLink = useSlotGroup('floorplanslist.map_link', mapLinkDef);
  const plans = useCollectionBinding<Floorplan>('floorplanslist.plans', { collection: 'floorplans' });

  const cards = toPlanCards(plans);
  const bedCounts = [...new Set(cards.map((c) => c.beds).filter((b): b is number => b != null))].sort((a, b) => a - b);
  const propertyAvail = num(p.property_available_units) || 0;
  const applyHref = apply.url || flags.applyUrl;

  const [beds, setBeds] = useState<number | 'all'>('all');
  const trackRef = useRef<HTMLDivElement>(null);
  const [arrows, setArrows] = useState({ show: false, prev: false, next: false });

  const arrowState = useCallback(() => {
    const t = trackRef.current;
    if (!t) return;
    const over = t.scrollWidth > t.clientWidth + 8;
    setArrows({ show: over, prev: t.scrollLeft > 4, next: t.scrollLeft + t.clientWidth < t.scrollWidth - 4 });
  }, []);

  useEffect(() => {
    const t = trackRef.current;
    if (!t) return;
    arrowState();
    t.addEventListener('scroll', arrowState, { passive: true });
    const ro = new ResizeObserver(arrowState);
    ro.observe(t);
    return () => {
      t.removeEventListener('scroll', arrowState);
      ro.disconnect();
    };
  }, [arrowState, cards.length]);

  useEffect(() => {
    trackRef.current?.scrollTo({ left: 0 });
    arrowState();
  }, [beds, arrowState]);

  const step = (dir: number) => {
    const t = trackRef.current;
    const card = t?.querySelector<HTMLElement>('.fpcard:not([hidden])');
    t?.scrollBy({ left: (card ? card.getBoundingClientRect().width + 24 : 400) * dir, behavior: reduced ? 'auto' : 'smooth' });
  };

  return (
    <section className="section section--surface plans" id="floor-plans">
      <div className="section__head" data-reveal>
        <div className="section__lead-block">
          <RichTextContent path="floorplanslist.eyebrow" as="div" className="eyebrow" />
          <RichTextContent path="floorplanslist.title" as="h2" className="h2" />
        </div>
        <RichTextContent path="floorplanslist.lead" as="p" className="section__lead" />
      </div>

      {bedCounts.length > 1 && (
        <div className="pills" role="group" aria-label="Filter floor plans by bedrooms">
          {(['all', ...bedCounts] as Array<number | 'all'>).map((b) => (
            <button key={String(b)} className="pillbtn" type="button" aria-pressed={beds === b} onClick={() => setBeds(b)}>
              {b === 'all' ? 'All' : `${b} bedroom`}
            </button>
          ))}
        </div>
      )}

      <EditableCollectionBinding bindingPath="floorplanslist.plans" label="Floor Plans">
        <div className="relative">
          {/* overflow-x: auto forces overflow-y to auto; the cards' reveal translateY then makes the track a vertical scroller that captures the page wheel. */}
          <div
            className="mx-[calc(-1*var(--gutter))] my-0 flex gap-6 overflow-x-auto px-[--gutter] pb-2 pt-1 [scroll-padding:0_var(--gutter)] [scroll-snap-type:x_mandatory] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            ref={trackRef}
            style={{ overflowY: 'hidden' }}
          >
            {cards.map((c, k) => {
              const facts = [c.beds != null && `${c.beds} bd`, c.baths != null && `${c.baths} ba`, c.sqft && `${range(c.sqft, c.sqftMax)} sq ft`].filter(Boolean).join(' · ');
              const avail = c.avail > 0 ? `${c.avail} ready now` : propertyAvail > 0 ? 'Ask about availability' : 'Join the waitlist';
              return (
                <article className="fpcard" key={c.key} hidden={beds !== 'all' && c.beds !== beds} data-reveal style={stagger(k % 3)}>
                  <a
                    className="relative block h-[260px] border-b border-b-[--q-border] bg-[--q-plan-surface] [&_img]:absolute [&_img]:inset-5 [&_img]:h-[calc(100%-40px)] [&_img]:w-[calc(100%-40px)] [&_img]:object-contain max-[767px]:h-[200px]"
                    href={PATHS.floorPlans}
                    aria-label={`${c.name} floor plan`}
                  >
                    {c.img ? <VibesitesImage url={c.img} alt={`${c.name} floor plan`} sizes="(min-width: 1200px) 33vw, (min-width: 768px) 50vw, 82vw" /> : PLAN_OUTLINE}
                  </a>
                  <div className="flex flex-col gap-3 px-6 pb-6 pt-[22px]">
                    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                      <h3 className="m-0 font-heading text-[28px] font-light leading-[34px] tracking-[--q-tracking-tight] max-[767px]:text-[24px] max-[767px]:leading-[30px]">
                        {c.name}
                      </h3>
                      {c.price ? (
                        <div className="flex items-baseline gap-1 whitespace-nowrap text-[13px] text-muted-foreground">
                          Starting at <b className="font-heading text-[24px] font-normal leading-7 text-[--q-secondary]">{money(c.price)}</b>/mo
                        </div>
                      ) : (
                        <div className="flex items-baseline gap-1 whitespace-nowrap text-[13px] text-muted-foreground">Call for pricing</div>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground">{facts}</div>
                    <div className="mt-1.5 flex items-center justify-between gap-3 border-t border-t-[--q-border] pt-3.5">
                      <span className={`text-sm ${c.avail > 0 ? 'font-medium text-[--q-ink]' : 'text-muted-foreground'}`}>{avail}</span>
                      <a className="textlink" href={PATHS.floorPlans}>See This Plan <span className="btn__arrow">→</span></a>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
          {arrows.show && (
            <div className="absolute right-0 top-[-76px] flex gap-2 [&_.iconbtn[disabled]]:pointer-events-none [&_.iconbtn[disabled]]:opacity-35 max-[767px]:hidden">
              <button className="iconbtn" type="button" aria-label="Previous plans" disabled={!arrows.prev} onClick={() => step(-1)}>←</button>
              <button className="iconbtn" type="button" aria-label="Next plans" disabled={!arrows.next} onClick={() => step(1)}>→</button>
            </div>
          )}
        </div>
      </EditableCollectionBinding>

      <div className="section__cta" data-reveal>
        <div className="section__note">
          <RichTextContent path="floorplanslist.note" as="span" />
          <EditableSlotGroup groupPath="floorplanslist.map_link" definition={mapLinkDef}>
            {flags.hasSightmap && (
              <span>
                {' · '}
                <a className="textlink" href={mapLink.url || PATHS.interactiveMap} target={mapLink._target || '_self'}>
                  {mapLink.label || 'View Availability on the Map'} <span className="btn__arrow">→</span>
                </a>
              </span>
            )}
          </EditableSlotGroup>
        </div>
        <div className="section__buttons">
          <EditableSlotGroup groupPath="floorplanslist.apply_cta" definition={applyCtaDef}>
            {applyHref && (
              <a className="btn btn-secondary" href={applyHref} target={apply._target || '_self'} data-vibesite-event={apply.vibesite_event || undefined}>
                {apply.label || 'Apply Now'}
              </a>
            )}
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="floorplanslist.explore_cta" definition={exploreCtaDef}>
            <a className="btn btn-primary" href={explore.url || PATHS.floorPlans} target={explore._target || '_self'}>
              {explore.label || 'Explore Floor Plans'} <span className="btn__arrow">→</span>
            </a>
          </EditableSlotGroup>
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FloorplansList',
  displayName: 'Floor Plans',
  tokens: {
    'floorplanslist.eyebrow': { label: 'Eyebrow', type: 'richtext', defaultValue: '<p>Floor plans</p>' },
    'floorplanslist.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Find your layout at <span data-type="field-token" data-field="property_name"></span></p>',
      suggestedFields: ['property_home_floorplans_blurb', 'property_floorplans_blurb'],
    },
    'floorplanslist.lead': {
      label: 'Lead',
      description: 'Mapped to the Home Floorplans Blurb field. Type over it to replace it, or pick another field from the suggestions.',
      type: 'richtext',
      defaultField: 'property_home_floorplans_blurb',
      defaultValue: '<p>Every plan with pricing and availability, updated daily.</p>',
      suggestedFields: ['property_home_floorplans_blurb', 'property_floorplans_blurb'],
    },
    'floorplanslist.note': { label: 'Footnote', type: 'richtext', defaultValue: '<p>Pricing and availability update daily</p>' },
  },
  slotGroups: {
    'floorplanslist.apply_cta': applyCtaDef,
    'floorplanslist.explore_cta': exploreCtaDef,
    'floorplanslist.map_link': mapLinkDef,
  },
  collectionBindings: {
    'floorplanslist.plans': {
      label: 'Floor Plans',
      collection: 'floorplans',
    },
  },
};

(FloorplansList as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FloorplansList;
